import { z } from 'zod'
import { EmailSchema, IsoDateTimeSchema, OkResponseSchema, UuidSchema } from '../primitives'
import { route } from '../route-def'

/**
 * Admin authentication and admin management (spec "Accounts and sessions"; AC7). The password policy (at least 12
 * characters, not a common password) is enforced by the API; the contract only bounds the input.
 */

export const ADMIN_PASSWORD_MIN = 12

export const AdminSummarySchema = z.object({ id: UuidSchema, email: z.string(), name: z.string() })
export type AdminSummary = z.infer<typeof AdminSummarySchema>

export const AdminLoginBodySchema = z.object({ email: EmailSchema, password: z.string().min(1).max(256) })
export type AdminLoginBody = z.infer<typeof AdminLoginBodySchema>

/**
 * Step 1 answer. Without `admin`: a code was emailed (the code step follows). With `admin`: this browser is a trusted
 * device of that admin (R109) and the session is already issued.
 */
export const AdminLoginResponseSchema = z.object({ ok: z.literal(true), admin: AdminSummarySchema.optional() })
export type AdminLoginResponse = z.infer<typeof AdminLoginResponseSchema>

export const AdminLoginVerifyBodySchema = z.object({ code: z.string().trim().min(1).max(12) })
export const AdminMeResponseSchema = z.object({ admin: AdminSummarySchema })
export type AdminMeResponse = z.infer<typeof AdminMeResponseSchema>

export const AdminInviteAcceptBodySchema = z.object({
  token: z.string().min(20).max(100),
  name: z.string().trim().min(1).max(100),
  password: z.string().max(256),
})
export type AdminInviteAcceptBody = z.infer<typeof AdminInviteAcceptBodySchema>

/** R108: "Forgot your password?" (the same answer whether or not the email belongs to an active admin). */
export const AdminPasswordForgotBodySchema = z.object({ email: EmailSchema })
/** R108: the emailed reset link (`/admin/reset#token=…`, 30 min, single use); the admin signs in afterwards. */
export const AdminPasswordResetBodySchema = z.object({
  token: z.string().min(20).max(100),
  password: z.string().max(256),
})
export type AdminPasswordResetBody = z.infer<typeof AdminPasswordResetBodySchema>

export const AdminListItemSchema = z.object({
  id: UuidSchema,
  email: z.string(),
  name: z.string(),
  status: z.enum(['active', 'revoked']),
  createdAt: IsoDateTimeSchema,
  revokedAt: IsoDateTimeSchema.nullable(),
})
export type AdminListItem = z.infer<typeof AdminListItemSchema>

export const AdminInviteItemSchema = z.object({
  id: UuidSchema,
  email: z.string(),
  invitedBy: UuidSchema.nullable(),
  createdAt: IsoDateTimeSchema,
  expiresAt: IsoDateTimeSchema,
})
export type AdminInviteItem = z.infer<typeof AdminInviteItemSchema>

export const AdminAdminsResponseSchema = z.object({
  admins: z.array(AdminListItemSchema),
  invites: z.array(AdminInviteItemSchema),
})
export type AdminAdminsResponse = z.infer<typeof AdminAdminsResponseSchema>

export const AdminInviteBodySchema = z.object({ email: EmailSchema })
export const AdminInviteResponseSchema = z.object({
  invite: z.object({ id: UuidSchema, email: z.string(), expiresAt: IsoDateTimeSchema }),
})

/** R109: a trusted device of the signed-in admin (`current`: the browser making the request). */
export const AdminDeviceSchema = z.object({
  id: UuidSchema,
  label: z.string(),
  createdAt: IsoDateTimeSchema,
  lastUsedAt: IsoDateTimeSchema,
  expiresAt: IsoDateTimeSchema,
  current: z.boolean(),
})
export type AdminDevice = z.infer<typeof AdminDeviceSchema>
export const AdminDevicesResponseSchema = z.object({ devices: z.array(AdminDeviceSchema) })
export type AdminDevicesResponse = z.infer<typeof AdminDevicesResponseSchema>

const IdParamsSchema = z.object({ id: UuidSchema })

export const adminAuthRoutes = {
  adminLogin: route({
    method: 'POST',
    path: '/api/admin/login',
    auth: 'public',
    body: AdminLoginBodySchema,
    response: AdminLoginResponseSchema,
  }),
  adminLoginResend: route({
    method: 'POST',
    path: '/api/admin/login/resend',
    auth: 'public',
    response: OkResponseSchema,
  }),
  adminLoginVerify: route({
    method: 'POST',
    path: '/api/admin/login/verify',
    auth: 'public',
    body: AdminLoginVerifyBodySchema,
    response: AdminMeResponseSchema,
  }),
  adminLogout: route({ method: 'POST', path: '/api/admin/logout', auth: 'admin', response: OkResponseSchema }),
  /** R109: "Forget this device": deletes this browser's trusted device, clears its cookie and logs out. */
  adminDeviceForget: route({
    method: 'POST',
    path: '/api/admin/devices/forget',
    auth: 'admin',
    response: OkResponseSchema,
  }),
  /** R109: the signed-in admin's own trusted devices. */
  adminDevices: route({
    method: 'GET',
    path: '/api/admin/devices',
    auth: 'admin',
    response: AdminDevicesResponseSchema,
  }),
  adminDeviceRevoke: route({
    method: 'POST',
    path: '/api/admin/devices/:id/revoke',
    auth: 'admin',
    params: IdParamsSchema,
    response: OkResponseSchema,
  }),
  adminDevicesRevokeAll: route({
    method: 'POST',
    path: '/api/admin/devices/revoke-all',
    auth: 'admin',
    response: OkResponseSchema,
  }),
  adminMe: route({ method: 'GET', path: '/api/admin/me', auth: 'admin', response: AdminMeResponseSchema }),
  adminInviteAccept: route({
    method: 'POST',
    path: '/api/admin/invites/accept',
    auth: 'public',
    body: AdminInviteAcceptBodySchema,
    response: OkResponseSchema,
  }),
  /** R108: public, CSRF-checked; always `{ ok: true }` (no enumeration). */
  adminPasswordForgot: route({
    method: 'POST',
    path: '/api/admin/password/forgot',
    auth: 'public',
    body: AdminPasswordForgotBodySchema,
    response: OkResponseSchema,
  }),
  /** R108: consumes the emailed token (POST only); does not sign the admin in. */
  adminPasswordReset: route({
    method: 'POST',
    path: '/api/admin/password/reset',
    auth: 'public',
    body: AdminPasswordResetBodySchema,
    response: OkResponseSchema,
  }),
  adminAdmins: route({ method: 'GET', path: '/api/admin/admins', auth: 'admin', response: AdminAdminsResponseSchema }),
  adminInvite: route({
    method: 'POST',
    path: '/api/admin/admins/invite',
    auth: 'admin',
    body: AdminInviteBodySchema,
    response: AdminInviteResponseSchema,
  }),
  adminRevoke: route({
    method: 'POST',
    path: '/api/admin/admins/:id/revoke',
    auth: 'admin',
    params: IdParamsSchema,
    response: OkResponseSchema,
  }),
  adminInviteRevoke: route({
    method: 'POST',
    path: '/api/admin/invites/:id/revoke',
    auth: 'admin',
    params: IdParamsSchema,
    response: OkResponseSchema,
  }),
}
